import axios from "axios";
import store from "../store";
import services from "./services";

function startRequest(name) {
  let requestCode = services.uuid();
  store.commit("startRequest", { name, code: requestCode });

  return requestCode;
}

function endRequest(code) {
  store.commit("endRequest", code);
}

function getToken() {
  return store.state.token;
}

export default {
  // Login
  login(username, password) {
    let code = startRequest("Logging in");
    return axios
      .post("/login", { username, password })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  checkToken(token = getToken()) {
    let code = startRequest("Checking token");
    return axios
      .post("/checkToken", { token })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },

  logout() {
    // Removes the token from the database, so it cannot be used again
    return axios.post("/logout", null, { headers: { token: getToken() } });
  },

  // Lessons
  getLessons(showMessage = true) {
    let code;
    if (showMessage) code = startRequest("Getting lessons");
    return axios
      .get("/lessons", { headers: { token: getToken() } })
      .finally(() => {
        if (showMessage) endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  getLesson(lessonName) {
    return axios
      .get("/lessons/" + lessonName + "/", { headers: { token: getToken() } })
      .then((response) => {
        return response.data;
      });
  },
  async addLesson(lesson) {
    console.log("Adding lesson: " + lesson.name);

    let formData = new FormData();
    formData.append("file", lesson.file);
    formData.append("filename", lesson.name);

    // File must be in body
    return axios
      .post("/lessons", formData, {
        headers: {
          token: getToken(),
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.loaded / progressEvent.total;
          lesson.uploadProgress = percentCompleted;
        },
      })
      .finally(() => {
        lesson.uploadProgress = 1;
      })
      .then((response) => {
        return response.data;
      });
  },
  deleteLesson(lessonName) {
    let code = startRequest("Deleting lesson");
    return axios
      .delete("/lessons/" + lessonName + "/", {
        headers: { token: getToken() },
      })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },

  // Requests
  getRequests() {
    let code = startRequest("Getting requests");
    return axios
      .get("/requests", { headers: { token: getToken() } })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  getRequest(requestId) {
    let code = startRequest("Getting request");
    return axios
      .get("/requests/" + requestId + "/", { headers: { token: getToken() } })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  getRequestFullResults(requestId) {
    let code = startRequest("Getting request full results");
    return axios
      .get("/requests/" + requestId + "/completeResults", {
        headers: { token: getToken() },
      })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  addRequest(prompt) {
    let code = startRequest("Requesting");
    return axios
      .post("/requests", { prompt }, { headers: { token: getToken() } })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },
  // Streaming variant of addRequest.
  //
  // The backend writes one JSON object per line as each step of the request
  // completes (the subjects, then the slides, then the answer token by token),
  // and onEvent is called with each of them as they arrive.
  //
  // fetch is used rather than axios because it exposes the response body as a
  // stream; axios buffers the whole response before resolving. A plain POST
  // carries this instead of a websocket: the data only flows one way, for the
  // length of one request, and it keeps the token header that EventSource
  // cannot send.
  async addRequestStreamed(prompt, onEvent) {
    const code = startRequest("Requesting");

    try {
      const response = await fetch("/requests/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: getToken(),
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        const error = new Error("Request failed");
        error.status = response.status;
        throw error;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const handleLine = (line) => {
        if (!line.trim()) return;
        onEvent(JSON.parse(line));
      };

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // A chunk can cut a line in half, so only whole lines are handed
        // over and the remainder waits for the next chunk.
        const lines = buffer.split("\n");
        buffer = lines.pop();
        lines.forEach(handleLine);
      }

      handleLine(buffer);
    } finally {
      endRequest(code);
    }
  },
  deleteRequest(requestId) {
    let code = startRequest("Deleting request");
    return axios
      .delete("/requests/" + requestId + "/", {
        headers: { token: getToken() },
      })
      .finally(() => {
        endRequest(code);
      })
      .then((response) => {
        return response.data;
      });
  },

  // Quick search
  quickSearch(keyword) {
    return axios
      .post("/quickSearch", { keyword }, { headers: { token: getToken() } })
      .then((response) => {
        return response.data;
      });
  },
};
